import { useNavigate } from 'react-router-dom'
import {
  ConfidenceBar,
  DataTable,
  EmptyState,
  PageHeader,
  StatusChip,
  Tag,
} from '@/design/components'
import type { Column } from '@/design/components'
import { EMPTY, formatDate, formatDateTime, plural } from '@/lib/format'
import { usePendingDocuments } from '../hooks'
import { SOURCE_LABEL, STATUS_LABEL, STATUS_VARIANT } from '../labels'
import type { DocumentRow } from '../types'

/** Lista dokumentów czekających na weryfikację (wejście do ekranu weryfikacji). */
export function VerificationListPage() {
  const navigate = useNavigate()
  const pending = usePendingDocuments(50)
  const rows = pending.data ?? []

  const columns: Column<DocumentRow>[] = [
    {
      key: 'title',
      header: 'Dokument',
      render: (row) => (
        <div className="min-w-0 leading-tight">
          <p className="truncate font-semibold text-ink">{row.title}</p>
          <p className="mt-0.5 truncate text-small text-muted">
            {SOURCE_LABEL[row.source]} · wpływ {formatDateTime(row.received_at)}
          </p>
        </div>
      ),
    },
    {
      key: 'doc_type',
      header: 'Typ (AI)',
      width: '150px',
      render: (row) => (row.doc_type ? <Tag>{row.doc_type}</Tag> : <span className="text-muted">{EMPTY}</span>),
    },
    {
      key: 'confidence',
      header: 'Pewność',
      width: '140px',
      render: (row) => <ConfidenceBar value={row.confidence} width={132} />,
    },
    {
      key: 'case',
      header: 'Sugerowana sprawa',
      width: '180px',
      render: (row) => {
        const target = row.case ?? row.suggested_case
        return target ? (
          <span className="t-mono text-ink">{target.signature}</span>
        ) : (
          <span className="t-mono text-danger">brak dopasowania</span>
        )
      },
    },
    {
      key: 'detected_deadline',
      header: 'Wykryty termin',
      width: '130px',
      render: (row) =>
        row.detected_deadline ? (
          <span className="font-medium text-danger">{formatDate(row.detected_deadline)}</span>
        ) : (
          <span className="text-muted">{EMPTY}</span>
        ),
    },
    {
      key: 'status',
      header: 'Status',
      width: '140px',
      render: (row) => (
        <StatusChip variant={STATUS_VARIANT[row.status]}>{STATUS_LABEL[row.status]}</StatusChip>
      ),
    },
  ]

  return (
    <>
      <PageHeader
        title="Weryfikacja dokumentu"
        subtitle={
          pending.data
            ? `${rows.length} ${plural(rows.length, 'dokument czeka', 'dokumenty czekają', 'dokumentów czeka')} na potwierdzenie danych z modelu.`
            : 'Wczytywanie…'
        }
      />
      <DataTable<DocumentRow>
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        onRowClick={(row) => navigate(`/dokumenty/weryfikacja/${row.id}`)}
        loading={pending.isPending}
        empty={
          <EmptyState
            title="Nic nie czeka na weryfikację"
            description="Wszystkie dokumenty ze skrzynki zostały potwierdzone."
          />
        }
      />
    </>
  )
}
